import { Event } from "../../../../../../pojo/Event";
import { TimeModelI } from "../../../../../../pojo/TimeModelI";
import { ProfileUtil } from "../../../../../../modules/s25-datepattern/profile.util";
import { DateTemplateRecord } from "../../../../../../services/date.template.service";
import { S25Util } from "../../../../../../util/s25-util";
import { AdditionalTimeType, AdditionalTimeUnit } from "./find-location.types";
import Occurrence = Event.Occurrence;

export const ADDITIONAL_TIME_TYPES: AdditionalTimeType[] = ["setup", "takedown"];
export const ADDITIONAL_TIME_UNITS: AdditionalTimeUnit[] = ["Days", "Hours", "Minutes"];
export const ADDITIONAL_TIME_UNIT_MAX: Record<AdditionalTimeUnit, number> = {
    Days: 999,
    Hours: 23,
    Minutes: 59,
};

export function additionalTimeLabel(timeType: AdditionalTimeType): string {
    return timeType.charAt(0).toUpperCase() + timeType.slice(1) + " Time";
}

export function createDefaultTimeModel(startDatetime: Date, endDatetime: Date): TimeModelI {
    const timeModel = ProfileUtil.emptyTimeModel(startDatetime, endDatetime);
    initAdditionalTimeFields(timeModel);
    return timeModel;
}

export function initAdditionalTimeFields(timeModel: TimeModelI): void {
    for (const timeType of ADDITIONAL_TIME_TYPES) {
        for (const unit of ADDITIONAL_TIME_UNITS) {
            const fieldKey = `_${timeType}${unit}`;
            timeModel[fieldKey] = timeModel[fieldKey] ?? 0;
        }
    }
}

export function refreshAdditionalTimeFieldsFromMinutes(timeModel: TimeModelI): void {
    if (!timeModel.minutes) {
        return;
    }
    for (const timeType of ADDITIONAL_TIME_TYPES) {
        let totalMinutes = timeModel.minutes[`${timeType}Minutes`] || 0;
        const days = Math.floor(totalMinutes / 24 / 60);
        totalMinutes -= days * 24 * 60;
        const hours = Math.floor(totalMinutes / 60);
        totalMinutes -= hours * 60;
        timeModel[`_${timeType}Days`] = days;
        timeModel[`_${timeType}Hours`] = hours;
        timeModel[`_${timeType}Minutes`] = totalMinutes;
    }
}

export function syncAdditionalTimeMinutesFromFields(timeModel: TimeModelI): void {
    if (!timeModel.minutes) {
        return;
    }
    for (const timeType of ADDITIONAL_TIME_TYPES) {
        timeModel.minutes[`${timeType}Minutes`] =
            (timeModel[`_${timeType}Days`] || 0) * 24 * 60 +
            (timeModel[`_${timeType}Hours`] || 0) * 60 +
            (timeModel[`_${timeType}Minutes`] || 0);
    }
}

export function resolveThroughDate(timeModel: TimeModelI, profileCode: string): Date {
    return (
        ProfileUtil.getProfileCodeThroughDate(profileCode) ||
        ProfileUtil.getDefaultThroughDate(timeModel) ||
        timeModel.evStartDt
    );
}

export function syncTemplateOccurrencesFromProfileCode(timeModel: TimeModelI, profileCode: string): Occurrence[] {
    if (["dnr", "adhoc"].includes(profileCode)) {
        return [];
    }
    const throughDate = resolveThroughDate(timeModel, profileCode);
    return ProfileUtil.getOccurrencesFromProfileCode(timeModel, profileCode, throughDate);
}

export type AppliedDateTemplate = {
    startDatetime: Date;
    endDatetime: Date;
    setupMinutes: number;
    takedownMinutes: number;
    profileCode: string;
    adHocOccurrences: Occurrence[];
};

export function parseDateTemplate(template: DateTemplateRecord): AppliedDateTemplate {
    const origStartDt = S25Util.date.parseDropTZ(template.init_start_dt);
    let newStartDt = S25Util.date.clone(origStartDt);
    let newEndDt = S25Util.date.parseDropTZ(template.init_end_dt);

    const setupRelDt = S25Util.date.parseDropTZ("1900-01-01T00:00:00");
    const setupDt = template.setup_tm ? S25Util.date.parseDropTZ(template.setup_tm) : undefined;
    const takedownDt = template.tdown_tm ? S25Util.date.parseDropTZ(template.tdown_tm) : undefined;

    let setupMinutes = 0;
    if (setupDt && S25Util.date.isValid(setupDt, true)) {
        setupMinutes = S25Util.date.diffMinutes(setupRelDt, setupDt);
    }
    let takedownMinutes = 0;
    if (takedownDt && S25Util.date.isValid(takedownDt, true)) {
        takedownMinutes = S25Util.date.diffMinutes(setupRelDt, takedownDt);
    }

    const now = new Date();
    if (newEndDt.getFullYear() < 2000 && newEndDt < now) {
        newEndDt = S25Util.date.clone(newEndDt);
        newEndDt.setFullYear(now.getFullYear());
        newStartDt = S25Util.date.addMinutes(newEndDt, -S25Util.date.diffMinutes(origStartDt, newEndDt));
    }

    let profileCode = "dnr";
    let adHocOccurrences: Occurrence[] = [];

    if (template.profile_code) {
        const days = S25Util.date.diffDays(origStartDt, newStartDt);
        profileCode = template.profile_code;
        const throughDate = ProfileUtil.getProfileCodeThroughDate(profileCode);
        if (throughDate) {
            profileCode =
                ProfileUtil.setThroughDateOnProfileCode(S25Util.date.addDays(throughDate, days), profileCode) ??
                profileCode;
        }
    } else if (template.adhoc?.length) {
        const adhocEntries = [{ start_dt: template.init_start_dt, end_dt: template.init_end_dt }, ...template.adhoc];
        const uniqueAdhoc = S25Util.array.uniqueDeep(adhocEntries);
        adHocOccurrences = uniqueAdhoc.map((adhoc) => ({
            evStartDt: S25Util.date.parseDropTZ(adhoc.start_dt),
            evEndDt: S25Util.date.parseDropTZ(adhoc.end_dt),
        }));
        profileCode = "adhoc";
    }

    return {
        startDatetime: newStartDt,
        endDatetime: newEndDt,
        setupMinutes,
        takedownMinutes,
        profileCode,
        adHocOccurrences,
    };
}

export function seedTemplateOccurrence(
    startDatetime: Date,
    endDatetime: Date,
    minutes: TimeModelI["minutes"],
): Occurrence[] {
    return [
        {
            evStartDt: S25Util.date.clone(startDatetime),
            evEndDt: S25Util.date.clone(endDatetime),
            minutes,
        },
    ];
}
